import { NavLinkButton, Separator } from '@ais-ui/ui-core';
import { useState } from 'react';
import { PiCaretLeftThin } from 'react-icons/pi';
import { ROUTES } from '../../routes';
import { SidebarNav } from './SidebarNav';

import { useLocalization } from '@ais-ui/locale-management';
import { SettingsAuthenticationPage } from './Authentication/AuthenticationSettings';
import { SettingsBaseMapPage } from './BaseMap/BaseMapSettings';
import { SettingsBookmarksPage } from './Bookmarks/BookmarksSettings';
import { SettingsEventsPage } from './Events/EventsSettings';
import { SettingsGeneralPage } from './General/GeneralSettings';
import { SettingsNotificationsPage } from './Notifications/NotificationSettings';
import { SettingsMapPage } from './Map/MapSettings';
import { SettingsMessagesPage } from './Messages/MessagesSettings';
import { SettingsWMSPage } from './Wms/WMSSettings';
import { SettingsTargetPage } from './Target/TargetSettings';
import { SettingsReportsPage } from './Reports/ReportsSettings';
import { SettingsVectorTilesPage } from './VectorTiles/VectorTileSettings';
import { useSettingsPage } from './SettingsPageProvider';
import { useNavigate } from 'react-router';

export enum SettingItem {
  General = 'GENERAL',
  WMS = 'WMS',
  VectorTiles = 'VECTOR_TILES',
  Notification = 'NOTIFICATION',
  Map = 'MAP',
  BaseMap = 'BASEMAP',
  Bookmarks = 'BOOKMARKS',
  Authentication = 'AUTHENTICATION',
  Messages = 'MESSAGES',
  Events = 'EVENTS',
  Target = 'TARGET',
  Reports = 'REPORTS',
}

interface SettingsPageProps {
  initialActiveSettingItem?: SettingItem;
}

export function SettingsPage({
  initialActiveSettingItem = SettingItem.General,
}: SettingsPageProps) {
  const { messages } = useLocalization();
  const [activeSettingItem, setActiveSettingItem] = useState<SettingItem>(
    initialActiveSettingItem
  );
  const navigate = useNavigate();
  const { checkNavigationSafety } = useSettingsPage();

  const onSettingItemClick = (settingItem: SettingItem) => {
    const onConfirm = () => {
      setActiveSettingItem(settingItem);
    };

    checkNavigationSafety(onConfirm);
  };

  const sidebarNavItems = [
    {
      id: SettingItem.General,
      title: messages.settingsSidebarGeneralTitle,
      onClick: () => onSettingItemClick(SettingItem.General),
    },
    {
      id: SettingItem.Notification,
      title: messages.settingsSidebarNotificationsTitle,
      onClick: () => onSettingItemClick(SettingItem.Notification),
    },
    {
      id: SettingItem.Map,
      title: messages.settingsSidebarMapTitle,
      onClick: () => onSettingItemClick(SettingItem.Map),
    },
    {
      id: SettingItem.WMS,
      title: messages.settingsSidebarWmsTitle,
      onClick: () => onSettingItemClick(SettingItem.WMS),
    },
    {
      id: SettingItem.VectorTiles,
      title: messages.settingsSidebarVectorTilesTitle,
      onClick: () => onSettingItemClick(SettingItem.VectorTiles),
    },
    {
      id: SettingItem.BaseMap,
      title: messages.settingsSidebarBaseMapTitle,
      onClick: () => onSettingItemClick(SettingItem.BaseMap),
    },
    {
      id: SettingItem.Target,
      title: messages.settingsTargetTitle,
      onClick: () => onSettingItemClick(SettingItem.Target),
    },

    {
      id: SettingItem.Events,
      title: messages.settingsEventsTitle,
      onClick: () => onSettingItemClick(SettingItem.Events),
    },
    {
      id: SettingItem.Messages,
      title: messages.settingsMessagesTitle,
      onClick: () => onSettingItemClick(SettingItem.Messages),
    },

    {
      id: SettingItem.Reports,
      title: messages.settingsReportsTitle,
      onClick: () => onSettingItemClick(SettingItem.Reports),
    },
    {
      id: SettingItem.Authentication,
      title: messages.settingsAuthenticationTitle,
      onClick: () => onSettingItemClick(SettingItem.Authentication),
    },
    {
      id: SettingItem.Bookmarks,
      title: messages.settingsBookmarksTitle,
      onClick: () => onSettingItemClick(SettingItem.Bookmarks),
    },
  ];

  return (
    <div className="flex justify-center">
      <div className="hidden space-y-6 p-10 pb-16 md:block w-[984px]">
        <div className="w-30">
          <NavLinkButton
            id="settings-viewMap-button"
            onClick={(e) => {
              e.preventDefault();
              checkNavigationSafety(() => navigate(ROUTES.MAP.path));
            }}
            to={ROUTES.MAP.path}
            className="p-0 m-0"
          >
            <PiCaretLeftThin className="p-0"></PiCaretLeftThin>
            <div className="pl-2">{messages.settingsGoBackToMap}</div>
          </NavLinkButton>
        </div>
        <div className="space-y-0.5">
          <h2 className="text-2xl font-bold tracking-tight">
            {messages.settingsTitle}
          </h2>
          <p className="text-muted-foreground">{messages.settingsSubtitle}</p>
        </div>
        <Separator className="my-6" />
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0 w-full">
          <aside>
            <SidebarNav
              items={sidebarNavItems}
              activeItem={activeSettingItem}
            />
          </aside>
          <div className="lg:max-w-2xl w-full">
            {activeSettingItem === SettingItem.General && (
              <SettingsGeneralPage></SettingsGeneralPage>
            )}
            {activeSettingItem === SettingItem.Notification && (
              <SettingsNotificationsPage></SettingsNotificationsPage>
            )}
            {activeSettingItem === SettingItem.Map && (
              <SettingsMapPage></SettingsMapPage>
            )}
            {activeSettingItem === SettingItem.BaseMap && (
              <SettingsBaseMapPage></SettingsBaseMapPage>
            )}
            {activeSettingItem === SettingItem.WMS && (
              <SettingsWMSPage></SettingsWMSPage>
            )}
            {activeSettingItem === SettingItem.VectorTiles && (
              <SettingsVectorTilesPage></SettingsVectorTilesPage>
            )}
            {activeSettingItem === SettingItem.Target && (
              <SettingsTargetPage></SettingsTargetPage>
            )}
            {activeSettingItem === SettingItem.Events && (
              <SettingsEventsPage></SettingsEventsPage>
            )}
            {activeSettingItem === SettingItem.Messages && (
              <SettingsMessagesPage></SettingsMessagesPage>
            )}
            {activeSettingItem === SettingItem.Reports && (
              <SettingsReportsPage></SettingsReportsPage>
            )}
            {activeSettingItem === SettingItem.Authentication && (
              <SettingsAuthenticationPage></SettingsAuthenticationPage>
            )}
            {activeSettingItem === SettingItem.Bookmarks && (
              <SettingsBookmarksPage></SettingsBookmarksPage>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
